import { QueryClient, type QueryFunction } from "@tanstack/react-query";

export { queryKeys } from "./keys";
export {
  buildPodcastPreviewRoute,
  dedupeWatchFeedItems,
  getPodcastContinueListeningQueryKey,
  getPodcastFeedQueryKey,
  getPodcastFeedWidgetQueryKey,
  getPodcastRecommendationsQueryKey,
  getPodcastsScreenMode,
  getPodcastDiscoveryHomeQueryKey,
  getPodcastStatusQueryKey,
  getPodcastSubscriptionsQueryKey,
  getStoriesQueryKey,
  getWatchFeedItemKey,
  getWatchFeedQueryKey,
  getWatchScreenMode,
  getWatchTabInteraction,
  shouldShowNewEpisodesFallback,
  togglePlaybackWithMusic,
  type NewEpisodesFallbackGate,
  type PodcastPreviewInput,
} from "./screen-helpers";

export type UnauthorizedBehavior = "returnNull" | "throw";

export const SCROLL_CLIENT_GENERATION_HEADER = "X-Scroll-Client-Generation";
export const CURRENT_SCROLL_CLIENT_GENERATION = "4";

export interface AuthTransport {
  getHeaders(): Promise<Record<string, string>>;
  getBaseUrl(): string;
  getFetchOptions?(): RequestInit;
}

export async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

/**
 * Decodes an Error message produced by `throwIfResNotOk` / `createApiRequest`
 * (format `"<status>: <body>"`, body usually JSON like `{"error":"..."}`) into
 * a status code and a short, human-readable detail. Inverse of the encoder
 * above — keep them together so the format only lives in one place.
 */
export function parseApiError(message: string | null | undefined): { status?: number; detail: string } {
  const raw = (message || "").trim();
  const match = raw.match(/^(\d{3}):\s*([\s\S]*)$/);
  const status = match ? parseInt(match[1], 10) : undefined;
  let detail = (match ? match[2] : raw).trim();
  try {
    const parsed = JSON.parse(detail);
    if (parsed && typeof parsed === "object") {
      if (typeof parsed.error === "string") detail = parsed.error;
      else if (typeof parsed.message === "string") detail = parsed.message;
    }
  } catch {
    // body wasn't JSON (e.g. an HTML error page) — keep the raw text
  }
  detail = detail.trim();
  if (!detail) detail = "Something went wrong.";
  if (detail.length > 200) detail = detail.slice(0, 200).trimEnd() + "…";
  return { status, detail };
}

export function createApiRequest(transport: AuthTransport) {
  return async function apiRequest(
    method: string,
    endpoint: string,
    data?: unknown,
    signal?: AbortSignal,
  ): Promise<Response> {
    const baseHeaders = await transport.getHeaders();
    const headers: Record<string, string> = {
      ...baseHeaders,
      [SCROLL_CLIENT_GENERATION_HEADER]: CURRENT_SCROLL_CLIENT_GENERATION,
    };
    if (data) headers["Content-Type"] = "application/json";

    const res = await fetch(`${transport.getBaseUrl()}${endpoint}`, {
      method,
      headers,
      body: data ? JSON.stringify(data) : undefined,
      signal,
      ...transport.getFetchOptions?.(),
    });

    await throwIfResNotOk(res);
    return res;
  };
}

export function createQueryFn<T>(
  transport: AuthTransport,
  options: { on401: UnauthorizedBehavior },
): QueryFunction<T> {
  return async ({ queryKey, signal }) => {
    const headers = {
      ...await transport.getHeaders(),
      [SCROLL_CLIENT_GENERATION_HEADER]: CURRENT_SCROLL_CLIENT_GENERATION,
    };

    const res = await fetch(
      `${transport.getBaseUrl()}${queryKey[0] as string}`,
      {
        headers,
        signal,
        ...transport.getFetchOptions?.(),
      },
    );

    if (options.on401 === "returnNull" && res.status === 401) {
      return null as T;
    }

    await throwIfResNotOk(res);
    return (await res.json()) as T;
  };
}

export const defaultQueryOptions = {
  queries: {
    refetchInterval: false as const,
    refetchOnWindowFocus: false,
    staleTime: 60_000,
    gcTime: 300_000,
    retry: false,
  },
  mutations: {
    retry: false,
  },
};

/** Critical auth query options that prevent the Clerk token refresh infinite loop */
export const authQueryOptions = {
  retry: false,
  staleTime: 5 * 60 * 1000,
  gcTime: 10 * 60 * 1000,
};

export function prependOptimisticInfiniteFeedItem<
  TItem extends { data?: unknown },
  TCache extends { pages?: unknown[] } | undefined,
>(cache: TCache, optimisticItem: TItem): TCache {
  if (!cache?.pages?.length) return cache;

  const firstPage = cache.pages[0] as any;
  const updatedFirstPage =
    firstPage && "items" in firstPage
      ? { ...firstPage, items: [optimisticItem, ...firstPage.items] }
      : { ...firstPage, posts: [optimisticItem.data, ...firstPage.posts] };

  return {
    ...cache,
    pages: [updatedFirstPage, ...cache.pages.slice(1)],
  };
}

export function createQueryClient(
  defaultQueryFn?: QueryFunction<unknown>,
  defaultOptionsOverride?: Partial<
    NonNullable<ConstructorParameters<typeof QueryClient>[0]>["defaultOptions"]
  >,
): QueryClient {
  return new QueryClient({
    defaultOptions: {
      ...defaultQueryOptions,
      ...defaultOptionsOverride,
      queries: {
        ...defaultQueryOptions.queries,
        ...defaultOptionsOverride?.queries,
        ...(defaultQueryFn ? { queryFn: defaultQueryFn } : {}),
      },
      mutations: {
        ...defaultQueryOptions.mutations,
        ...defaultOptionsOverride?.mutations,
      },
    },
  });
}
